import { Caption1, Spinner, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Alert16Regular, Mail16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMockPlanAccess } from "@/features/access/mock-plan-access";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { ReceivedInvitesDialog } from "@/features/inbox/InboxRows";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatAgo } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { activityPath } from "@/features/activity/activity-open";
import { projectIdFromPath } from "@/shell/project-nav";
import { buildNoticeMock, NOTICE_LOAD_MS, type NoticeItem, type NoticeKind } from "./notice-mock";

export type NoticeFilter = "all" | NoticeKind;

const useStyles = makeStyles({
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  on: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
    color: tokens.colorNeutralForeground1,
  },
  dot: {
    position: "absolute",
    top: "6px",
    right: "6px",
    width: "6px",
    height: "6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandForeground1,
    pointerEvents: "none",
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    maxHeight: "380px",
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    padding: "6px 8px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  readAllBtn: {
    fontSize: tokens.fontSizeBase200,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    height: "24px",
    minHeight: "24px",
    color: tokens.colorNeutralForeground3,
    ":hover": {
      color: tokens.colorNeutralForeground1,
    },
  },
  inviteBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: `6px ${tokens.spacingHorizontalM}`,
    border: "none",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFast,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  inviteText: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground1,
  },
  inviteAction: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    color: tokens.colorNeutralForeground3,
  },
  empty: {
    flex: "none",
    minHeight: "auto",
  },
  list: {
    minHeight: 0,
    overflow: "auto",
    padding: "4px 0",
  },
  rowBtn: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    width: "100%",
    padding: `6px ${tokens.spacingHorizontalM}`,
    border: "none",
    backgroundColor: "transparent",
    textAlign: "left",
    cursor: "pointer",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFast,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-1px",
    },
  },
  text: {
    minWidth: 0,
    flex: 1,
  },
  title: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  titleRead: {
    color: tokens.colorNeutralForeground2,
  },
  meta: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground3,
  },
  unread: {
    width: "6px",
    height: "6px",
    marginTop: "6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  spacer: {
    width: "6px",
    flexShrink: 0,
  },
  all: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "100%",
    height: "32px",
    border: "none",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
});

/** 창 맨 위 알림. 팝오버 완결형: 분류 필터, 모두 읽음, 클릭 시 해당 플랜으로 이동. */
export function InboxBell() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const access = useMockPlanAccess();
  const projects = useCatalogStore((s) => s.projects);
  const byProject = useCatalogStore((s) => s.byProject);

  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useState<NoticeFilter>("all");
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [notices, setNotices] = useState<NoticeItem[]>(() => buildNoticeMock());

  const pendingInvites = access.incoming.length;
  const unreadNotices = notices.filter((item) => item.unread).length;
  const totalUnread = unreadNotices + pendingInvites;

  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setReady(true);
      return;
    }
    const timer = window.setTimeout(() => setReady(true), NOTICE_LOAD_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const visibleNotices = useMemo(() => {
    if (filter === "all") return notices;
    return notices.filter((n) => n.kind === filter);
  }, [notices, filter]);

  const markAllAsRead = () => {
    setNotices((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const handleNoticeClick = (item: NoticeItem) => {
    // 1. 읽음 처리
    setNotices((prev) => prev.map((n) => (n.id === item.id ? { ...n, unread: false } : n)));
    setOpen(false);

    // 2. 연결된 플랜 찾기 (planId 우선, 없으면 plan 이름으로 매핑)
    let targetId = item.planId && projects[item.planId] ? item.planId : undefined;
    if (!targetId) {
      const match = Object.values(projects).find((p) => p.name === item.plan);
      if (match) targetId = match.id;
    }

    if (targetId) {
      const lastTab = byProject[targetId]?.lastTab ?? "schedule";
      const store = useProjectStore.getState();
      if (store.activeId !== targetId) store.openProject(targetId);
      navigate(`/projects/${targetId}/${lastTab}`);
    } else {
      // 매칭되는 플랜이 없는 경우 플랜 목록 화면으로 이동
      navigate("/");
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={mergeClasses(styles.btn, open && styles.on)}
            aria-label={totalUnread > 0 ? `알림, 읽지 않은 소식 ${totalUnread}건` : "알림"}
            aria-haspopup="dialog"
            aria-expanded={open}
            title="알림"
          >
            <Alert16Regular />
            {totalUnread > 0 ? <span className={styles.dot} aria-hidden /> : null}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="w-[360px] overflow-hidden p-0" onOpenAutoFocus={(event) => event.preventDefault()}>
          <div className={styles.panel} aria-busy={!ready}>
            {/* 상단 필터 및 모두 읽음 액션 바 */}
            <div className={styles.filterBar}>
              <SegmentedControl
                value={filter}
                onChange={setFilter}
                ariaLabel="알림 종류"
                options={[
                  { value: "all", label: "전체" },
                  { value: "delay", label: "지연" },
                  { value: "progress", label: "진척" },
                ]}
              />
              {unreadNotices > 0 ? (
                <FuiButton appearance="subtle" className={styles.readAllBtn} onClick={markAllAsRead}>
                  모두 읽음
                </FuiButton>
              ) : null}
            </div>

            {/* 대기 중인 초대가 있을 때 상단 연결 배너 */}
            {pendingInvites > 0 && (
              <button
                type="button"
                className={styles.inviteBanner}
                onClick={() => {
                  setOpen(false);
                  setInvitesOpen(true);
                }}
              >
                <span className={styles.inviteText}>
                  <Mail16Regular />
                  <span>받은 초대 {pendingInvites}건</span>
                </span>
                <Caption1 className={styles.inviteAction}>확인하기 →</Caption1>
              </button>
            )}

            {ready ? (
              visibleNotices.length > 0 ? (
                <div className={styles.list} role="list" aria-label="알림">
                  {visibleNotices.map((item) => {
                    const meta = `${item.author} · ${item.plan} · ${formatAgo(item.at)}`;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={styles.rowBtn}
                        role="listitem"
                        onClick={() => handleNoticeClick(item)}
                      >
                        <PersonAvatar name={item.author} size={24} />
                        <div className={styles.text}>
                          <Tooltip content={item.title} relationship="description">
                            <span className={mergeClasses(styles.title, !item.unread && styles.titleRead)}>{item.title}</span>
                          </Tooltip>
                          <Tooltip content={meta} relationship="description">
                            <Caption1 className={styles.meta}>{meta}</Caption1>
                          </Tooltip>
                        </div>
                        {item.unread ? <span className={styles.unread} aria-hidden /> : <span className={styles.spacer} aria-hidden />}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <GhostEmptyState
                  className={styles.empty}
                  compact
                  title={filter === "all" ? "알림이 없습니다" : "해당 알림이 없습니다"}
                  description={filter === "all" ? "새 소식이 오면 여기에 모입니다." : "다른 구분을 선택해 보세요."}
                />
              )
            ) : (
              <div className={styles.loading} role="status">
                <Spinner size="extra-small" />
                <Caption1>소식을 불러오는 중</Caption1>
              </div>
            )}
            <button
              type="button"
              className={styles.all}
              onClick={() => {
                setOpen(false);
                const planId = projectIdFromPath(location.pathname);
                navigate(activityPath(planId && projects[planId] ? planId : null));
              }}
            >
              모두 보기
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <ReceivedInvitesDialog open={invitesOpen} onClose={() => setInvitesOpen(false)} />
    </>
  );
}
